import React, { useState } from "react";

type ModalContent = {
  title: string;
  subtitle?: string | JSX.Element; 
  showCancelButton?: boolean;
  acceptButtonText?: string;
  cancelButtonText?: string;
  icon?: JSX.Element | React.ReactNode;
};

type Actions = {
  onAccept: (...args: any[]) => any;
  onClose?: (...args: any[]) => any;
  onCancel?: (...args: any[]) => any;
};

export type TAlertContextType = {
  show: boolean;
  content: ModalContent | null;
  actions: Actions | null;
  isLoading: boolean;
  showAlert: (content: ModalContent | string, actions?: Actions) => void;
  hideAlert: () => void;
  setLoading: (loading: boolean) => void;
}; 

const modalInitialState = {
  show: false,
  actions: null,
  content: null,
  isLoading: false,
  showAlert: () => {},
  hideAlert: () => {},
  setLoading: () => {},
};
export const AlertContext =
  React.createContext<TAlertContextType>(modalInitialState);

type ModalState = Omit<TAlertContextType, "showAlert" | "hideAlert" | "isLoading" | "setLoading">;

export const AlertProvider = ({ children }: any) => {
  const [modal, setModal] = useState<ModalState>(modalInitialState);
  const [isLoading, setIsLoading] = useState(false);

  const hideAlert = () => {
    setModal(modalInitialState);
    setIsLoading(false);
  };

  const showAlert = (
    content: ModalContent | string,
    actions?: Actions | null,
  ) => {
    const newContent =
      typeof content === "string" ? { title: content } : content;
    setModal(() => ({
      show: true,
      content: newContent,
      actions: actions || null,
    }));
    setIsLoading(false);
  };

  const setLoading = (loading: boolean) => setIsLoading(loading);

  const value = {
    ...modal,
    isLoading,
    hideAlert,
    showAlert,
    setLoading,
  };

  return (
    <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
  );
};
